/**
 * Competition-aware route building (specs/competitions-hub-client Slice 3).
 *
 * The World Cup keeps its own un-parameterized tree at /app/worldcup; every other
 * competition lives under /app/competition/<id>. All route strings in the shared
 * squad/leaderboard screens are built from a `basePath` so one component serves both.
 */

import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import {
  WORLD_CUP_BASE_PATH,
  type CompetitionSurface,
} from '../contexts/competitionContext.constants';

/**
 * `knockout` is a real route on the World Cup surface (`app/app/worldcup/knockout.tsx`) and
 * has no counterpart on the generic one, which is why it stays here while `standings` does
 * not: the standings pane was briefly a tab and shipped as a header modal instead
 * (`CompetitionStandingsModal`), so no tree ever produced that segment.
 */
export type CompetitionTab = 'picks' | 'leaderboard' | 'groups' | 'knockout';

const COMPETITION_TABS: readonly CompetitionTab[] = [
  'picks', 'leaderboard', 'groups', 'knockout',
];

export function isCompetitionTab(value?: string | null): value is CompetitionTab {
  return COMPETITION_TABS.includes(value as CompetitionTab);
}

/** WC → its legacy tree; anything else → the parameterized tree. Never trailing-slashed. */
export function competitionBasePath(competitionId: string): string {
  if (competitionId === WORLD_CUP_COMPETITION_ID) return WORLD_CUP_BASE_PATH;
  return `/app/competition/${encodeURIComponent(competitionId)}`;
}

/** Picks is the index route of the tree, so it maps to the base path itself. */
export function competitionTabRoute(basePath: string, tab: CompetitionTab): string {
  return tab === 'picks' ? basePath : `${basePath}/${tab}`;
}

export function competitionGroupsRoute(basePath: string): string {
  return `${basePath}/groups`;
}

export function competitionGroupRoute(basePath: string, groupId: string): string {
  return `${competitionGroupsRoute(basePath)}/${encodeURIComponent(groupId)}`;
}

export function competitionGroupCreateRoute(basePath: string): string {
  return `${competitionGroupsRoute(basePath)}/create`;
}

export function competitionGroupJoinRoute(
  basePath: string,
  opts?: { code?: string | null; autojoin?: boolean },
): string {
  const base = `${competitionGroupsRoute(basePath)}/join`;
  const params: string[] = [];
  if (opts?.code) params.push(`code=${encodeURIComponent(opts.code)}`);
  if (opts?.autojoin) params.push('autojoin=1');
  return params.length > 0 ? `${base}?${params.join('&')}` : base;
}

export function competitionMemberProfileRoute(basePath: string): string {
  return `${basePath}/member-profile`;
}

/**
 * Match hub and pick-stats routes (specs/champions-league AC-10.3). Both trees carry the same
 * two groups, so building from `basePath` returns the World Cup's existing URLs unchanged and
 * gives every other competition its own.
 */
export function competitionMatchRoute(basePath: string, matchId: string): string {
  return `${basePath}/match/${encodeURIComponent(matchId)}`;
}

export function competitionMatchStatsRoute(basePath: string, matchId: string): string {
  return `${basePath}/match-stats/${encodeURIComponent(matchId)}`;
}

/**
 * Where a member-profile back button lands with no history to pop (refresh / deep link):
 * the originating squad, else the originating tab, else picks.
 */
export function resolveCompetitionBackRoute(
  basePath: string,
  groupId?: string | null,
  tab?: string | null,
): string {
  if (groupId) return competitionGroupRoute(basePath, groupId);
  return competitionTabRoute(basePath, isCompetitionTab(tab) ? tab : 'picks');
}

function parseTabParam(
  searchParams?: Record<string, string | string[] | undefined>,
): CompetitionTab | null {
  const raw = searchParams?.wcTab;
  const value = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : undefined;
  return isCompetitionTab(value) ? value : null;
}

/**
 * Which tab the underline highlights, for either surface.
 *
 * Matched on path SEGMENTS rather than a `basePath` prefix: `usePathname()` and the
 * route builder can disagree on URL-encoding of the competition id, which would break
 * a string-prefix comparison.
 *   worldcup: /app/worldcup/<tab>            → segment after 'worldcup'
 *   generic:  /app/competition/<id>/<tab>    → segment two after 'competition'
 */
export function getActiveCompetitionTab(
  pathname: string,
  surface: CompetitionSurface,
  searchParams?: Record<string, string | string[] | undefined>,
): CompetitionTab {
  const segments = pathname.split('/').filter(Boolean);
  let after: string | undefined;

  if (surface === 'worldcup') {
    const index = segments.indexOf('worldcup');
    after = index >= 0 ? segments[index + 1] : undefined;
  } else {
    const index = segments.indexOf('competition');
    after = index >= 0 ? segments[index + 2] : undefined;
  }

  // member-profile is not nested under groups/, so the originating tab rides along as a param.
  if (after === 'member-profile') return parseTabParam(searchParams) ?? 'picks';

  return isCompetitionTab(after) ? after : 'picks';
}
